import React, { useState, useRef, useEffect } from 'react';
import { Bot, Maximize2, Minimize2, X, Trash2, Ticket } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TicketForm } from './TicketForm';

export function ChatWindow({
  messages,
  isLoading,
  onSendMessage,
  onClearHistory,
  onClose,
  isFullScreen,
  onToggleFullScreen,
  onTicketCreated,
}) {
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketFormSubject, setTicketFormSubject] = useState('');
  const bodyRef = useRef(null);

  // Keep chat at top on initial welcome message, and auto-scroll to bottom as conversation progresses
  useEffect(() => {
    if (!bodyRef.current) return;
    if (messages.length <= 1 && !showTicketForm) {
      bodyRef.current.scrollTop = 0;
    } else {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, showTicketForm]);

  // Allow pressing Escape to exit full-screen mode
  useEffect(() => {
    if (!isFullScreen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onToggleFullScreen) {
        onToggleFullScreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, onToggleFullScreen]);

  const handleTicketSubmitted = (ticket) => {
    setShowTicketForm(false);
    setTicketFormSubject('');
    if (onTicketCreated) {
      onTicketCreated(ticket);
    }
  };

  const deriveSubjectFromSuggestion = (suggestionText = '') => {
    const cleaned = suggestionText
      .replace(/^🎫\s*/i, '')
      .replace(/^raise a ticket\s*(?:to|for)?\s*/i, '')
      .trim();
    if (!cleaned) return 'General Inquiry / Support';
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const handleSelectQuestion = (question) => {
    if (isLoading) return;
    const lower = (question || '').toLowerCase();
    if (
      lower.includes('raise a ticket') ||
      lower.includes('submit a support ticket') ||
      lower.includes('submit an inquiry ticket') ||
      lower.includes('open ticket form')
    ) {
      setTicketFormSubject(deriveSubjectFromSuggestion(question));
      setShowTicketForm(true);
      return;
    }
    onSendMessage(question);
  };

  const handleClearHistory = () => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
    onClearHistory();
  };

  return (
    <div className={`chat-window ${isFullScreen ? 'fullscreen extended' : ''}`}>
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="agent-avatar-badge">
            <Bot size={20} />
            <span className="status-dot" title="Online"></span>
          </div>
          <div className="chat-title-group">
            <h3>Sagveek Assistant</h3>
            <span className="chat-subtitle">Official Sagveek Support • Active</span>
          </div>
        </div>

        <div className="chat-header-actions">
          {/* Quick Support Ticket Button */}
          <button
            onClick={() => {
              setTicketFormSubject('');
              setShowTicketForm((prev) => !prev);
            }}
            className={`icon-btn ${showTicketForm ? 'active-btn' : ''}`}
            title="Open Support Ticket Form"
            aria-label="Open Support Ticket Form"
          >
            <Ticket size={15} />
          </button>
          <button
            onClick={handleClearHistory}
            className="icon-btn"
            title="Clear conversation"
            aria-label="Clear conversation"
          >
            <Trash2 size={15} />
          </button>
          {/* Extend / Collapse Chatbot Button */}
          <button
            onClick={onToggleFullScreen}
            className={`icon-btn ${isFullScreen ? 'active-btn' : ''}`}
            title={isFullScreen ? 'Collapse Chatbot' : 'Extend Chatbot'}
            aria-label={isFullScreen ? 'Collapse Chatbot' : 'Extend Chatbot'}
          >
            {isFullScreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button
            onClick={onClose}
            className="icon-btn"
            title="Close chat"
            aria-label="Close chat"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Body & Input */}
      <div className="chat-body" ref={bodyRef}>
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            onSelectQuestion={handleSelectQuestion}
            isLoading={isLoading}
          />
        ))}

        {/* In-chat interactive ticket form */}
        {showTicketForm && (
          <TicketForm
            initialSubject={ticketFormSubject}
            onClose={() => {
              setShowTicketForm(false);
              setTicketFormSubject('');
            }}
            onTicketCreated={handleTicketSubmitted}
          />
        )}
      </div>

      <ChatInput
        onSendMessage={onSendMessage}
        isLoading={isLoading}
        showSuggestions={false}
        onOpenTicketForm={() => {
          setTicketFormSubject('');
          setShowTicketForm(true);
        }}
      />
    </div>
  );
}
